import { ReactNode } from 'react';
import BottomBarWrapper from '@campaigns/src/components/bottom-bar/BottomBarWrapper';
import { Button, Icon } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { FLOWS_PRODUCTS_PATH } from '@flows/src/FlowsProductsRouter';
import useAnvaFlowProductSelectionStore from '@flows/src/store/zustand/flows-products/useAnvaFlowProductSelectionStore';
import BottomBarBasketActions from '@flows/src/components/flow-products/quotes/basket/BottomBarBasketActions';
import BottomBarSelectionOverviewActions from '@flows/src/components/flow-products/quotes/basket/BottomBarSelectionOverviewActions';
import { useFlowProductsOverviewParams } from '@flows/src/hooks/flow-products/useFlowProductsOverviewParams';
import { useAnvaQuotesV2Simple } from '@flows/src/api/anva-flows/getAnvaQuotesV2';
import { AnvaQuoteWithJob } from '@flows/src/types/anva';
import useQueryParams from '@common/src/hooks/useQueryParams';
import { isCampaignsUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';

interface Props {
  handlePrevious?: () => void;
  backText?: string;
}

const AnvaFlowProductsBottomBar = ({ handlePrevious, backText }: Props) => {
  const { t } = useTranslation();
  const { flowId } = useParams<{ flowId: string }>();
  // Determine the current and next category (tag) so we can cycle through
  const { tagId } = useFlowProductsOverviewParams();
  const queryParams = useQueryParams();
  const sessionId = queryParams.get('session_id') || undefined;
  const { data: quotesData } = useAnvaQuotesV2Simple({
    sessionId,
    enabled: !!sessionId,
  });

  // Build an ordered list of unique tag ids based on the quotes data
  const quotes = (quotesData?.quotes ?? []) as AnvaQuoteWithJob[];
  const categoryIds: string[] = [];
  for (const quote of quotes) {
    const id = quote.tag.id.toString();
    if (!categoryIds.includes(id)) {
      categoryIds.push(id);
    }
  }

  let currentIndex = -1;
  if (tagId) {
    currentIndex = categoryIds.indexOf(tagId);
  }
  const isIndexValid = (idx: number): boolean => idx >= 0;
  const isCurrentIndexValid = isIndexValid(currentIndex);
  let isLastCategory = true;
  if (isCurrentIndexValid) {
    isLastCategory = currentIndex === categoryIds.length - 1;
  }
  const hasNextCategory =
    !isLastCategory &&
    currentIndex >= 0 &&
    currentIndex + 1 < categoryIds.length;
  let nextCategoryId: string | null = null;
  if (hasNextCategory) {
    nextCategoryId = categoryIds[currentIndex + 1];
  }
  const isSelectionOverviewPage =
    window.location.pathname.includes('selection-overview');
  const isBasketPage = window.location.pathname.includes('basket');
  const isBasketEmpty = useAnvaFlowProductSelectionStore(
    (state) => !(state.selectedItems.length > 0),
  );
  const history = useHistory();

  // NEW: navigate back to dashboard when on selection overview page
  const handleDashboardNavigation = () => {
    history.push('/dashboard');
  };

  const handleNavigation = () => {
    history.push({
      pathname: `${FLOWS_PRODUCTS_PATH}/${flowId}/basket`,
      search: window.location.search,
    });
  };

  const handleNextCategoryNavigation = () => {
    if (nextCategoryId) {
      history.push({
        pathname: `${FLOWS_PRODUCTS_PATH}/${flowId}/overview/${nextCategoryId}`,
        search: window.location.search,
      });
    } else {
      // Fallback to basket navigation when no further categories are available
      handleNavigation();
    }
  };

  // Determine previous category for back navigation
  const isFirstCategory = !isCurrentIndexValid || currentIndex === 0;
  const prevCategoryId =
    !isFirstCategory && currentIndex > 0 ? categoryIds[currentIndex - 1] : null;

  const handlePrevCategoryNavigation = () => {
    if (prevCategoryId) {
      history.push({
        pathname: `${FLOWS_PRODUCTS_PATH}/${flowId}/overview/${prevCategoryId}`,
        search: window.location.search,
      });
    }
  };

  // Compose left/back button
  let leftButton: ReactNode = null;

  if (isSelectionOverviewPage) {
    // Handled separately further down (Back to dashboard)
  } else if (isBasketPage) {
    // Keep original behaviour for basket page
    leftButton = (
      <Button
        flexShrink={0}
        onClick={handlePrevious}
        leftIcon={<Icon name="GeneralActionsArrowLeft" />}
        variant="white"
      >
        {backText}
      </Button>
    );
  } else {
    // Overview page – cycle through categories or go back to questions
    const label = isFirstCategory ? backText : t('Previous');
    const onClick = isFirstCategory
      ? handlePrevious
      : handlePrevCategoryNavigation;

    leftButton = (
      <Button
        flexShrink={0}
        onClick={onClick}
        leftIcon={<Icon name="GeneralActionsArrowLeft" />}
        variant="white"
      >
        {label}
      </Button>
    );
  }

  // Compose primary (right) action button for overview page
  let rightButton: ReactNode = null;

  if (!isBasketPage && !isSelectionOverviewPage) {
    const disableButton = isLastCategory ? isBasketEmpty : false;
    const onClick = isLastCategory
      ? handleNavigation
      : handleNextCategoryNavigation;
    const labelKey = isLastCategory
      ? 'CAMPAIGNS_DETAIL-CHOSEN_PRODUCTS'
      : 'NEXT';

    rightButton = (
      <Button
        flexShrink={0}
        ml="auto"
        onClick={onClick}
        isDisabled={disableButton}
        rightIcon={<Icon name="GeneralActionsArrowRight" />}
      >
        {t(labelKey)}
      </Button>
    );
  }

  return (
    <BottomBarWrapper>
      {isSelectionOverviewPage && !isCampaignsUrl && (
        <Button
          flexShrink={0}
          onClick={handleDashboardNavigation}
          leftIcon={<Icon name="GeneralActionsArrowLeft" />}
          variant="white"
        >
          {t('BACK-TO-DASHBOARD')}
        </Button>
      )}
      {leftButton}
      {rightButton}
      {isBasketPage && <BottomBarBasketActions />}
      {isSelectionOverviewPage && (
        <BottomBarSelectionOverviewActions flowId={flowId} />
      )}
    </BottomBarWrapper>
  );
};

export default AnvaFlowProductsBottomBar;
